/**
 * Utility functions for interacting with the Irish spellchecker API
 */

import logger from './logger';

// Use the main API instead of connecting directly to the spellcheck service
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
logger.debug('API Base URL:', API_BASE_URL);

/**
 * Checks Irish text for spelling errors using the spellcheck API
 * @param {string} text - The Irish text to check
 * @param {boolean} ignoreFada - Whether to ignore words that differ only by accents (fadas)
 * @returns {Promise<Object>} - Object with results containing misspelled words and suggestions
 */
export const checkIrishSpelling = async (text, ignoreFada = false) => {
  if (!text || text.trim() === '') {
    logger.debug('Empty text provided to spellchecker, returning empty results');
    return { results: [], raw_output: '', errors: '' };
  }
  
  logger.debug(`Sending text to spellchecker: Length=${text.length} chars, Sample="${text.substring(0, 50)}...", ignoreFada=${ignoreFada}`);
  
  try {
    const payload = { 
      text,
      ignore_fada: ignoreFada
    };
    
    // Get auth token from wherever it's stored in your application
    const authToken = localStorage.getItem('token'); // Adjust based on your auth storage
    
    if (!authToken) {
      logger.warn('No auth token found for spellcheck request');
    } else {
      logger.warn('Auth token available for spellcheck request');
    }
    
    logger.debug(`Sending spellcheck request to: ${API_BASE_URL}/api/spellcheck`);
    
    const response = await fetch(`${API_BASE_URL}/api/spellcheck`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authToken ? `Bearer ${authToken}` : '',
      },
      body: JSON.stringify(payload),
      credentials: 'include' // Include cookies if needed
    });

    logger.debug('Response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      logger.error(`API error response (${response.status}):`, errorText);
      throw new Error(`Spellcheck API error: ${response.status} - ${errorText || response.statusText}`);
    }

    const data = await response.json();
    logger.debug(`Spellcheck results: Found ${data.results?.length || 0} issues`);
    
    // Check if we got empty results with no error message
    if (!data.results || data.results.length === 0) {
      logger.debug('No spelling errors found in the text');
    }
    
    return data;
  } catch (error) {
    logger.error('Error checking Irish spelling:', error);
    // Return empty results instead of throwing to prevent UI breaking
    return { 
      results: [], 
      raw_output: '', 
      errors: error.message,
      errorObject: error.toString()
    };
  }
};

/**
 * Applies highlighting to misspelled words in a text
 * @param {string} text - Original text
 * @param {Array} spellcheckResults - Results from the spellcheck API
 * @returns {string} - HTML string with highlighted misspelled words
 */
export const highlightMisspelledWords = (text, spellcheckResults) => {
  // Validate inputs first
  if (!text) {
    logger.debug('Empty text provided to highlightMisspelledWords');
    return '';
  }
  
  logger.debug('Highlighting misspelled words, input:', { 
    textLength: text?.length || 0,
    hasResults: Boolean(spellcheckResults?.results?.length),
    resultCount: spellcheckResults?.results?.length || 0,
    textSample: text.substring(0, Math.min(100, text.length)) + (text.length > 100 ? '...' : '')
  });
  
  // Check if we have any results to process
  if (!spellcheckResults || !spellcheckResults.results || !Array.isArray(spellcheckResults.results) || spellcheckResults.results.length === 0) {
    logger.debug('No spelling errors found or empty results');
    return text;
  }

  try {
    // Sort errors by position in reverse order to avoid position shifts
    const sortedErrors = [...spellcheckResults.results].sort((a, b) => b.position - a.position);
    logger.debug(`Processing ${sortedErrors.length} spelling errors`);
    
    // Safety check - if we have more than 1000 errors something is probably wrong
    if (sortedErrors.length > 1000) {
      logger.warn(`Unusually high number of spelling errors (${sortedErrors.length}), limiting to first 1000`);
      sortedErrors.length = 1000;
    }
    
    // Simplify - directly highlight each error at its reported position
    let highlightedText = text;
    let successCount = 0;
    let positionMismatchCount = 0;
    let rangeErrorCount = 0;
    
    // Process errors in reverse order to avoid position shifts
    for (const error of sortedErrors) {
      try {
        // Verify error object structure
        if (!error || typeof error !== 'object') {
          logger.debug('Invalid error object in results:', error);
          continue;
        }
        
        const position = error.position;
        const word = error.word;
        
        // Validate error properties
        if (!word || typeof word !== 'string' || typeof position !== 'number') {
          logger.debug(`Invalid error data: word="${word}", position=${position}`);
          continue;
        }
        
        if (position < 0 || position >= highlightedText.length) {
          logger.debug(`Error with word "${word}" at position ${position} - out of range (text length: ${highlightedText.length})`);
          rangeErrorCount++;
          continue;
        }
        
        // Get actual word at position
        const endPosition = Math.min(position + word.length, highlightedText.length);
        const actualWord = highlightedText.substring(position, endPosition);
        
        // Check if the word matches what we expect
        if (actualWord.toLowerCase() === word.toLowerCase()) {
          // Generate tooltip with suggestions
          const suggestions = error.suggestions && Array.isArray(error.suggestions) ? error.suggestions : [];
          const tooltipContent = suggestions.length > 0
            ? `data-suggestions="${suggestions.slice(0, 5).join(', ')}"` 
            : 'data-suggestions="No suggestions available"';
          
          // Insert highlight span
          const before = highlightedText.substring(0, position);
          const after = highlightedText.substring(position + word.length);
          
          highlightedText = 
            `${before}<span class="misspelled-word" ${tooltipContent} data-word="${word}">${actualWord}</span>${after}`;
            
          successCount++;
        } else {
          logger.debug(`Position mismatch for "${word}" at ${position}, found "${actualWord}" instead`);
          positionMismatchCount++;
        }
      } catch (err) {
        logger.error(`Error processing misspelled word:`, err);
      }
    }
    
    logger.debug(`Highlighting complete: ${successCount} words highlighted, ${positionMismatchCount} position mismatches, ${rangeErrorCount} range errors`);
    
    // If we couldn't highlight any words (despite having results), return the original text
    if (successCount === 0 && sortedErrors.length > 0) {
    logger.warn('No words were successfully highlighted despite having error results');
    }
    
    return highlightedText;
  } catch (error) {
    logger.error('Error in highlightMisspelledWords:', error);
    // Return the original text if there's an error
    return text;
  }
};

/**
 * Gets a plain text representation from HTML with highlights
 * @param {string} htmlText - HTML text with highlight spans
 * @returns {string} - Plain text without HTML tags
 */
export const getPlainTextFromHighlighted = (htmlText) => {
  // Create a temporary div to hold the HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlText;
  
  // Return the text content, which strips all HTML tags
  return tempDiv.textContent;
}; 